import * as THREE from "three";
import type { Vec3 } from "@rietz/shared";
import { BIRD_COLOR } from "../render/palette.js";

/**
 * The birds a spooked animal puts up. Not scenery: `birdFlush` is broadcast to
 * EVERYONE, so it is the one thing that tells a stranger across half a valley,
 * with no shot fired, that somebody is walking through that treeline. It has to
 * be visible at range, brief and unmistakable — a dozen dark things going up
 * over one point — so they are UNLIT and nearly black: a bird against a bright
 * sky is a moving hole in the light. Fixed pools (`game/fx.ts`), the OLDEST
 * flush stolen when full. No DOM.
 */

/**
 * Concurrent flushes. Three is two hunters at opposite ends plus a spare; a
 * fourth steals the oldest.
 */
const MAX_FLUSHES = 3;

/**
 * Birds per flush. The pool is built at the maximum and only `count` go up — a
 * flock of fixed size is a flock nobody believes twice.
 */
const BIRDS_MIN = 8;
const BIRDS_MAX = 12;

/** How long the whole thing lasts. Long enough to see from across a field,
 *  short enough that the information has an expiry date. */
const FLUSH_LIFE_MS = 3000;

/** Wingspan, metres. A wood pigeon rather than a sparrow: this has to read at
 *  eighty metres, and a sparrow-sized quad is one pixel there. */
const BIRD_SIZE_M = 0.34;

/** How far above the animal the birds start — they were sitting in the tree it
 *  walked under, not on its back. */
const START_UP_MIN_M = 1;
const START_UP_MAX_M = 3;

/** Launch speed, metres per second. */
const SPEED_MIN = 4;
const SPEED_MAX = 7;

/**
 * How much of the launch speed is UP rather than out. Well over half: birds
 * leave a tree up first, and a flat fan reads as something thrown.
 */
const RISE_MIN = 0.55;
const RISE_MAX = 0.95;

/** Per-second velocity decay. Air, roughly: they burst off the branch and then
 *  settle into a climb rather than accelerating away forever. */
const DRAG_PER_SEC = 0.55;

/** Wingbeats per second, and how far the wings fold at the bottom of one. */
const FLAP_HZ_MIN = 5;
const FLAP_HZ_MAX = 8;
const FLAP_FOLD = 0.45;

/**
 * The flock holds full opacity for this fraction of its life, then fades — a
 * flush fading immediately would be half gone before anybody turned their head.
 */
const HOLD_FRACTION = 0.35;

/** One flush: a pool of birds, their velocities, and the clock they live on. */
interface Flock {
  /** Milliseconds left. `<= 0` means the slot is free. */
  life: number;
  /** Recycling order — see `claim`. */
  born: number;
  /** How many of `birds` are up this time. */
  count: number;
  /** One material per FLOCK rather than per bird: every bird in a flush fades
   *  on the same clock, so twelve materials would be eleven wasted uploads. */
  mat: THREE.MeshBasicMaterial;
  birds: THREE.Mesh[];
  vel: { x: number; y: number; z: number }[];
  /** Wingbeat phase and rate, per bird — a flock beating in unison is a
   *  flock that reads as one object. */
  flapPhase: number[];
  flapHz: number[];
}

/**
 * A bird, as two triangles in a shallow V: one metre across, nose toward −Z,
 * scaled to `BIRD_SIZE_M`; the flap is a scale on X. Double-sided, since from
 * underneath is the common case.
 */
function makeWingGeometry(): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  // nose, left tip, right tip, tail — the V is the two tips lifted above the
  // line from nose to tail.
  const verts = new Float32Array([
    // left wing
    0, 0, -0.5,
    -0.5, 0.18, 0.2,
    0, 0, 0.5,
    // right wing
    0, 0, -0.5,
    0, 0, 0.5,
    0.5, 0.18, 0.2,
  ]);
  geo.setAttribute("position", new THREE.BufferAttribute(verts, 3));
  geo.computeVertexNormals();
  return geo;
}

export class BirdFlush {
  private readonly scene: THREE.Scene;
  private readonly root = new THREE.Group();
  private readonly geo: THREE.BufferGeometry;
  private readonly flocks: Flock[] = [];
  /** Monotonic birth counter, for oldest-wins recycling. */
  private stamp = 0;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.root.name = "fx:birds";
    scene.add(this.root);

    this.geo = makeWingGeometry();

    for (let f = 0; f < MAX_FLUSHES; f++) {
      const mat = new THREE.MeshBasicMaterial({
        color: BIRD_COLOR,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      const flock: Flock = {
        life: 0,
        born: 0,
        count: 0,
        mat,
        birds: [],
        vel: [],
        flapPhase: [],
        flapHz: [],
      };
      for (let b = 0; b < BIRDS_MAX; b++) {
        const mesh = new THREE.Mesh(this.geo, mat);
        mesh.visible = false;
        // The flock is scattered across a hundred metres of sky by the end of
        // its life; a bounding sphere computed once would be permanently wrong.
        mesh.frustumCulled = false;
        this.root.add(mesh);
        flock.birds.push(mesh);
        flock.vel.push({ x: 0, y: 0, z: 0 });
        flock.flapPhase.push(0);
        flock.flapHz.push(FLAP_HZ_MIN);
      }
      this.flocks.push(flock);
    }
  }

  /**
   * Something broke cover here: eight to twelve birds go up. `pos` is the
   * ANIMAL's feet; the birds start one to three metres above it.
   */
  flush(pos: Vec3): void {
    const flock = this.flocks[this.claim()];
    if (flock === undefined) return;

    flock.life = FLUSH_LIFE_MS;
    flock.born = ++this.stamp;
    flock.count = BIRDS_MIN + Math.floor(Math.random() * (BIRDS_MAX - BIRDS_MIN + 1));
    flock.mat.opacity = 1;

    for (let b = 0; b < flock.birds.length; b++) {
      const mesh = flock.birds[b];
      const vel = flock.vel[b];
      if (mesh === undefined || vel === undefined) continue;
      if (b >= flock.count) {
        mesh.visible = false;
        continue;
      }

      // Fanned around the point rather than started at it: one pixel reads as a
      // firework, a metre or two as a hedge emptying.
      const ang = Math.random() * Math.PI * 2;
      const rad = Math.random() * 1.5;
      mesh.position.set(
        pos.x + Math.cos(ang) * rad,
        pos.y + START_UP_MIN_M + Math.random() * (START_UP_MAX_M - START_UP_MIN_M),
        pos.z + Math.sin(ang) * rad,
      );

      // Outward along its own bearing (so the flock OPENS) and mostly upward,
      // each
      // bird with its own speed and rise — what keeps the fan from being a
      // cone.
      const speed = SPEED_MIN + Math.random() * (SPEED_MAX - SPEED_MIN);
      const rise = RISE_MIN + Math.random() * (RISE_MAX - RISE_MIN);
      const out = Math.sqrt(Math.max(0, 1 - rise * rise));
      const bearing = ang + (Math.random() - 0.5) * 0.9;
      vel.x = Math.cos(bearing) * out * speed;
      vel.y = rise * speed;
      vel.z = Math.sin(bearing) * out * speed;

      flock.flapPhase[b] = Math.random() * Math.PI * 2;
      flock.flapHz[b] = FLAP_HZ_MIN + Math.random() * (FLAP_HZ_MAX - FLAP_HZ_MIN);
      mesh.scale.setScalar(BIRD_SIZE_M);
      mesh.visible = true;
      faceVelocity(mesh, vel);
    }
  }

  update(dtMs: number): void {
    const dt = Math.min(Math.max(dtMs, 0), 100) / 1000;

    for (const flock of this.flocks) {
      if (flock.life <= 0) continue;
      flock.life -= dtMs;
      if (flock.life <= 0) {
        flock.life = 0;
        flock.mat.opacity = 0;
        for (const mesh of flock.birds) mesh.visible = false;
        continue;
      }

      const k = 1 - flock.life / FLUSH_LIFE_MS;
      // Holds, then fades. Squared on the way out so the flock thins into the
      // sky rather than switching off at the end of its hold.
      const fade =
        k <= HOLD_FRACTION ? 1 : 1 - (k - HOLD_FRACTION) / (1 - HOLD_FRACTION);
      flock.mat.opacity = fade * fade;

      const decay = Math.max(0, 1 - DRAG_PER_SEC * dt);
      for (let b = 0; b < flock.count; b++) {
        const mesh = flock.birds[b];
        const vel = flock.vel[b];
        const phase = flock.flapPhase[b];
        const hz = flock.flapHz[b];
        if (mesh === undefined || vel === undefined) continue;
        if (phase === undefined || hz === undefined) continue;

        mesh.position.x += vel.x * dt;
        mesh.position.y += vel.y * dt;
        mesh.position.z += vel.z * dt;
        vel.x *= decay;
        vel.y *= decay;
        vel.z *= decay;

        const next = phase + hz * dt * Math.PI * 2;
        flock.flapPhase[b] = next > Math.PI * 2 ? next - Math.PI * 2 : next;
        // The beat is a fold in the WINGSPAN; a bird bobbing up and down reads
        // as a
        // hovering insect.
        mesh.scale.x = BIRD_SIZE_M * (1 - FLAP_FOLD * (0.5 + 0.5 * Math.sin(next)));
        faceVelocity(mesh, vel);
      }
    }
  }

  /**
   * Puts every flock down at once — leaving a raid, as `Fx.clearSmoke` does.
   */
  clear(): void {
    for (const flock of this.flocks) {
      flock.life = 0;
      flock.mat.opacity = 0;
      for (const mesh of flock.birds) mesh.visible = false;
    }
  }

  dispose(): void {
    this.scene.remove(this.root);
    for (const flock of this.flocks) flock.mat.dispose();
    this.root.clear();
    this.flocks.length = 0;
    this.geo.dispose();
  }

  /** Oldest-wins recycling, exactly as `game/fx.ts` does it. */
  private claim(): number {
    let oldest = 0;
    let oldestBorn = Infinity;
    for (let i = 0; i < this.flocks.length; i++) {
      const flock = this.flocks[i];
      if (flock === undefined) continue;
      if (flock.life <= 0) return i;
      if (flock.born < oldestBorn) {
        oldestBorn = flock.born;
        oldest = i;
      }
    }
    return oldest;
  }
}

/**
 * Points a bird along its velocity, yaw only. Rotating the −Z nose about Y by θ
 * gives (−sinθ, 0, −cosθ), so the bearing is `atan2(−vx, −vz)`.
 */
function faceVelocity(
  mesh: THREE.Mesh,
  vel: { x: number; y: number; z: number },
): void {
  if (vel.x === 0 && vel.z === 0) return;
  mesh.rotation.y = Math.atan2(-vel.x, -vel.z);
}
